import { defineStore } from 'pinia';
import type { RouteLocationNormalizedLoaded } from 'vue-router';
import {
  ETabBarSegments,
  BASIC_SEGMENT_DATA,
  PRODUCT_SEGMENT,
  CHECKOUT_SEGMENT,
  SEARCH_SEGMENT,
} from '~/modules/tabBar/configs/tabBarConfig';
import type { TSegmentedHistory, IRouteItem, TAllowedSegments } from '~/modules/tabBar/configs/tabBarConfig';
import {
  popLastPathFromActiveSegment,
  getPreLastPathFromActiveSegmentAndDeleteLast,
  getLastStepFromActiveSegment,
  clearAllHistoryInSegmented,
  addStepToHistory,
} from '~/modules/tabBar/utils/tabBarNavigationUtils';

export const tabBarNavigateStore = defineStore('tabBarStore', () => {
  const segmentedHistory = reactive<TSegmentedHistory>(BASIC_SEGMENT_DATA);
  const activeSegment = ref<ETabBarSegments>(ETabBarSegments.Catalog);

  const isRouteBlocked = ref<boolean>(true);

  const popLastPath = () => popLastPathFromActiveSegment(segmentedHistory, activeSegment.value);
  const getPreLastPath = () => getPreLastPathFromActiveSegmentAndDeleteLast(segmentedHistory, activeSegment.value);
  const getLastStep = () => getLastStepFromActiveSegment(segmentedHistory, activeSegment.value);
  const clearHistory = (segment: ETabBarSegments) => clearAllHistoryInSegmented(segmentedHistory, segment);
  const addStep = (step: IRouteItem) => addStepToHistory(segmentedHistory, activeSegment.value, step);
  const setActiveSegment = (segment: ETabBarSegments) => {
    activeSegment.value = segment;
  };

  const getBasicRouteActiveSegment = (): IRouteItem => {
    return BASIC_SEGMENT_DATA[activeSegment.value];
  };

  const getActiveRouteSegment = (): IRouteItem => {
    return segmentedHistory[activeSegment.value];
  };

  const setupActiveSegment = (value: RouteLocationNormalizedLoaded) => {
    const _normalizeSegment = (value: string) => {
      return value === '' ? 'home' : value;
    };

    const firstSegment = _normalizeSegment(value.path.split('/')[1]);
    let searched: IRouteItem;

    if (firstSegment === PRODUCT_SEGMENT) {
      searched = BASIC_SEGMENT_DATA.catalog;

    } else if (firstSegment === CHECKOUT_SEGMENT) {
      searched = BASIC_SEGMENT_DATA.cart;

      // } else if (firstSegment === SEARCH_SEGMENT) {
      //   searched = BASIC_SEGMENT_DATA.search;

    } else {
      searched = Object.values(BASIC_SEGMENT_DATA).find(
        (segment) => segment.segment === firstSegment,
      ) || BASIC_SEGMENT_DATA.unUsed;
    }

    activeSegment.value = searched.segment as ETabBarSegments;
  };

  const formattedRouteByIRouteItem = (path: string): IRouteItem => {
    const splitPath = path.split('/').filter(Boolean);

    const segment = splitPath[0] as TAllowedSegments;
    const code = splitPath[1] || null;

    return {
      segment: segment,
      code: code,
      path: path,
      scrollYTop: window?.scrollY || 0,
    };
  };

  const searchSegmentByPath = (path: string): TAllowedSegments | undefined => {
    return Object.values(BASIC_SEGMENT_DATA)
      .find((item) => item.path === path)
      ?.segment;
  };

  return {
    activeSegment,
    isRouteBlocked,
    segmentedHistory,
    addStepToHistory: addStep,
    popLastPathFromActiveSegment: popLastPath,
    getLastStepFromActiveSegment: getLastStep,
    getPreLastPathFromActiveSegmentAndDeleteLast: getPreLastPath,
    clearAllHistoryInSegmented: clearHistory,
    getBasicRouteActiveSegment,
    getActiveRouteSegment,
    setupActiveSegment,
    formattedRouteByIRouteItem,
    searchSegmentByPath,
    setActiveSegment,
  };
});
